import { appIntl } from '@nxsys/helpers/Common';
import {
  PaginationType,
} from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { paymentSettingActs } from '../action';
import { Deduction, GetDeductionListParams, GetPaymentListParams, GetPaymentRateListParams, GetVATCodeListParams, Payment, PaymentRate, VATCode } from '../types';

export interface PaymentSettingState {
  paymentList: {
    loading: boolean;
    list: Payment[];
    pagination: PaginationType;
    queryParams: GetPaymentListParams | null;
  };
  deductionList: {
    loading: boolean;
    list: Deduction[];
    pagination: PaginationType;
    queryParams: GetDeductionListParams | null;
  };
  paymentRateList: {
    loading: boolean;
    list: PaymentRate[];
    pagination: PaginationType;
    queryParams: GetPaymentRateListParams | null;
  };
  vatCodeList: {
    loading: boolean;
    list: VATCode[];
    pagination: PaginationType;
    queryParams: GetVATCodeListParams | null;
  };
}

const initialState: PaymentSettingState = {
  paymentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  deductionList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  paymentRateList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  vatCodeList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const paymentSettingSlice = createSlice({
  name: 'paymentSetting',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Payment Records
    builder.addCase(paymentSettingActs.getPaymentList.pending, (state) => {
      state.paymentList.loading = true;
    });
    builder.addCase(paymentSettingActs.getPaymentList.fulfilled, (state, action) => {
      state.paymentList.loading = false;
      state.paymentList.list = action.payload.data;
      state.paymentList.pagination = action.payload.pagination;
      state.paymentList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(paymentSettingActs.getPaymentList.rejected, (state, action) => {
      state.paymentList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.payment.getListError' }),
      });
    });

    // Get Payment Record
    builder.addCase(paymentSettingActs.getPaymentById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.payment.getDetailError' }),
      });
    });

    // Update Payment Record
    builder.addCase(paymentSettingActs.updatePaymentById.pending, () => {});
    builder.addCase(paymentSettingActs.updatePaymentById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.payment.updateSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.updatePaymentById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.payment.updateError' }),
      });
    });

    // Create Payment Record
    builder.addCase(paymentSettingActs.createPayment.pending, () => {});
    builder.addCase(paymentSettingActs.createPayment.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.payment.createSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.createPayment.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.payment.createError' }),
      });
    });

    // Get Deduction Records
    builder.addCase(paymentSettingActs.getDeductionList.pending, (state) => {
      state.deductionList.loading = true;
    });
    builder.addCase(paymentSettingActs.getDeductionList.fulfilled, (state, action) => {
      state.deductionList.loading = false;
      state.deductionList.list = action.payload.data;
      state.deductionList.pagination = action.payload.pagination;
      state.deductionList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(paymentSettingActs.getDeductionList.rejected, (state, action) => {
      state.deductionList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.deduction.getListError' }),
      });
    });

    // Get Deduction Record
    builder.addCase(paymentSettingActs.getDeductionById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.deduction.getDetailError' }),
      });
    });

    // Update Deduction Record
    builder.addCase(paymentSettingActs.updateDeductionById.pending, () => {});
    builder.addCase(paymentSettingActs.updateDeductionById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.deduction.updateSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.updateDeductionById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.deduction.updateError' }),
      });
    });

    // Create Deduction Record
    builder.addCase(paymentSettingActs.createDeduction.pending, () => {});
    builder.addCase(paymentSettingActs.createDeduction.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.deduction.createSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.createDeduction.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.deduction.createError' }),
      });
    });

    // Get Payment Rate Records
    builder.addCase(paymentSettingActs.getPaymentRateList.pending, (state) => {
      state.paymentRateList.loading = true;
    });
    builder.addCase(paymentSettingActs.getPaymentRateList.fulfilled, (state, action) => {
      state.paymentRateList.loading = false;
      state.paymentRateList.list = action.payload.data;
      state.paymentRateList.pagination = action.payload.pagination;
      state.paymentRateList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(paymentSettingActs.getPaymentRateList.rejected, (state, action) => {
      state.paymentRateList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.paymentRate.getListError' }),
      });
    });

    // Create Payment Rate Record
    builder.addCase(paymentSettingActs.createPaymentRate.pending, () => {});
    builder.addCase(paymentSettingActs.createPaymentRate.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.paymentRate.createSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.createPaymentRate.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.paymentRate.createError' }),
      });
    });

    // Update Payment Rate Record
    builder.addCase(paymentSettingActs.updatePaymentRateById.pending, () => {});
    builder.addCase(paymentSettingActs.updatePaymentRateById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'paymentSetting.paymentRate.updateSuccess' }),
      });
    });
    builder.addCase(paymentSettingActs.updatePaymentRateById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.paymentRate.updateError' }),
      });
    });

    // Get Payment Rate Record
    builder.addCase(paymentSettingActs.getPaymentRateById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.paymentRate.getDetailError' }),
      });
    });

    // Get VAT Code Records
    builder.addCase(paymentSettingActs.getVATCodeList.pending, (state) => {
      state.vatCodeList.loading = true;
    });
    builder.addCase(paymentSettingActs.getVATCodeList.fulfilled, (state, action) => {
      state.vatCodeList.loading = false;
      state.vatCodeList.list = action.payload.data;
      state.vatCodeList.pagination = action.payload.pagination;
      state.vatCodeList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(paymentSettingActs.getVATCodeList.rejected, (state, action) => {
      state.vatCodeList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'paymentSetting.vatCode.getListError' }),
      });
    });
  },
});

export const paymentSettingReducer = paymentSettingSlice.reducer;
export const paymentSettingActions = {
  ...paymentSettingSlice.actions,
  ...paymentSettingActs,
};
